<script lang="ts" setup>
import { useAuthStore } from '~/store/auth';
import { useSnackbarStore } from '~/store/snackbar';

const show = defineModel<boolean>({ required: true });

const { t } = useI18n();
const { changePassword } = useAuthStore();
const { showMessage } = useSnackbarStore();

const formValid = ref(false);
const currentPassword = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const loading = ref(false);

watch(show, value => {
  if (value) {
    currentPassword.value = '';
    newPassword.value = '';
    confirmPassword.value = '';
  }
});

const differentFromCurrent = (v: string) =>
  v !== currentPassword.value || t('components.change_password_dialog.same_as_current');

const submit = async () => {
  if (!formValid.value || loading.value) {
    return;
  }

  loading.value = true;
  const changed = await changePassword(currentPassword.value, newPassword.value);
  loading.value = false;

  if (changed) {
    showMessage({ content: t('components.change_password_dialog.changed'), color: 'success' });
    show.value = false;
  }
};
</script>

<template>
  <v-dialog
    v-model="show"
    max-width="480"
  >
    <v-card rounded="lg">
      <v-card-title class="pa-6 pb-2 text-subtitle-1 font-weight-bold">
        {{ t('components.change_password_dialog.title') }}
      </v-card-title>

      <v-form
        v-model="formValid"
        @submit.prevent="submit"
      >
        <v-card-text class="pa-6 pt-4 d-flex flex-column ga-4">
          <m-text-field
            v-model="currentPassword"
            :rules="[$rules.required]"
            type="password"
            autocomplete="current-password"
            :label="t('components.change_password_dialog.current_password')"
          />
          <m-text-field
            v-model="newPassword"
            :rules="[$rules.required, $rules.password, differentFromCurrent]"
            type="password"
            autocomplete="new-password"
            :label="t('components.change_password_dialog.new_password')"
          />
          <m-text-field
            v-model="confirmPassword"
            :rules="[$rules.required, $rules.confirmPassword(newPassword, confirmPassword)]"
            type="password"
            autocomplete="new-password"
            :label="t('components.change_password_dialog.new_password_confirm')"
          />
          <p class="text-body-2 text-medium-emphasis mb-0">
            {{ t('components.change_password_dialog.note') }}
          </p>
        </v-card-text>

        <v-card-actions class="pa-6 pt-0 justify-end">
          <m-btn
            variant="outlined"
            @click="show = false"
          >
            {{ t('buttons.cancel') }}
          </m-btn>
          <m-btn
            color="primary"
            type="submit"
            :loading="loading"
          >
            {{ t('buttons.update') }}
          </m-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>
